// src/pages/Home.jsx
import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Autoplay } from "swiper/modules";

import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/autoplay";

import Contact from "../components/Contact";
import testimonials from "../data/testimonials.json";

const pub = process.env.PUBLIC_URL;

/* ──────────────────────────────── click-outside hook ─────────────── */
function useClickOutside(ref, handler) {
  useEffect(() => {
    const listener = e => {
      if (ref.current && !ref.current.contains(e.target)) handler();
    };
    document.addEventListener("mousedown", listener);
    return () => document.removeEventListener("mousedown", listener);
  }, [ref, handler]);
}

/* ─────────────────────────────── interactive cards ───────────────── */
function ServiceCard({ icon, title, description, to }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useClickOutside(ref, () => setOpen(false));

  return (
    <div className="box" ref={ref}>
      <i className={`fas fa-${icon}`} />
      <h3>{title}</h3>
      <p>{description}</p>

      <div className="service-dropdown">
        <button
          className="dropdown-btn"
          aria-haspopup="true"
          aria-expanded={open}
          onClick={() => setOpen(o => !o)}
        >
          <i className="fas fa-ellipsis-v" />
        </button>

        {open && (
          <div className="dropdown-content active">
            {to && (
              <Link to={to} onClick={() => setOpen(false)}>
                View Details
              </Link>
            )}
            <button type="button">Request Quote</button>
            <button type="button">Download Brochure</button>
          </div>
        )}
      </div>
    </div>
  );
}

function ProjectCard({ src, title, subtitle }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useClickOutside(ref, () => setOpen(false));

  return (
    <div className="box" ref={ref}>
      <div className="image">
        <img src={`${pub}/${src}`} alt={title} />
      </div>
      <div className="content">
        <div className="info">
          <h3>{title}</h3>
          <p>{subtitle}</p>
        </div>
        <i className="fas fa-plus" />
      </div>

      <div className="project-dropdown">
        <button
          className="dropdown-btn"
          aria-haspopup="true"
          aria-expanded={open}
          onClick={() => setOpen(o => !o)}
        >
          <i className="fas fa-ellipsis-h" />
        </button>

        {open && (
          <div className="dropdown-content active">
            <button type="button">View Project</button>
            <button type="button">View Similar Projects</button>
            <button type="button">Contact Team</button>
          </div>
        )}
      </div>
    </div>
  );
}

/* ─────────────── CLIENT LOGOS — AUTO-SCROLL MARQUEE ─────────────── */
function Clients() {
  const pub = process.env.PUBLIC_URL;

  // Wire-up to generated files in /public/clients/client-01..09
  const items = [
    { name: "Client 01", slug: "client-01", url: "#" },
    { name: "Client 02", slug: "client-02", url: "#" },
    { name: "Client 03", slug: "client-03", url: "#" },
    { name: "Client 04", slug: "client-04", url: "#" },
    { name: "Client 05", slug: "client-05", url: "#" },
    { name: "Client 06", slug: "client-06", url: "#" },
    { name: "Client 07", slug: "client-07", url: "#" },
    { name: "Client 08", slug: "client-08", url: "#" },
    { name: "Client 09", slug: "client-09", url: "#" },
  ].map(({ name, slug, url }) => ({
    name,
    url,
    gray:  `${pub}/clients/${slug}/gray/128.png`,
    color: `${pub}/clients/${slug}/color/128.png`,
  }));

  // Duplicate for seamless loop
  const track = [...items, ...items];

  return (
    <section className="clients marquee" id="clients">
      {/* Scoped styles for drop-in */}
      <style>{`
        .clients.marquee { padding: 3rem 0; background:#fff; }
        .clients.marquee .heading { text-transform:none; margin:0 0 1rem; text-align:center; }

        .marquee-viewport {
          --scroll-speed: 45s;   /* increase for slower scroll */
          --gap: 48px;
          overflow: hidden;
          position: relative;
          width: 100%;
          padding: .5rem 0;
        }
        .marquee-track {
          display: flex;
          align-items: center;
          gap: var(--gap);
          list-style: none;
          padding: 0;
          margin: 0;
          width: max-content;
          animation: marquee-slide var(--scroll-speed) linear infinite;
        }
        /* left -> right */
        @keyframes marquee-slide {
          from { transform: translateX(-50%); }
          to   { transform: translateX(0%); }
        }
        .marquee-item a {
          position: relative;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          height: 72px;
          padding: 4px 8px;
        }
        .marquee-item .logo {
          height: 64px;
          max-width: 180px;
          width: auto;
          object-fit: contain;
          transition: opacity .25s ease;
          display: block;
        }
        .marquee-item .color { position: absolute; inset: 4px 8px; opacity: 0; }
        .marquee-item .gray  { opacity: .75; filter: grayscale(100%); }
        .marquee-item:hover .gray  { opacity: 0; }
        .marquee-item:hover .color { opacity: 1; }

        .marquee-viewport:hover .marquee-track { animation-play-state: paused; }

        @media (max-width: 640px) {
          .marquee-viewport { --scroll-speed: 55s; --gap: 28px; }
          .marquee-item .logo { height: 56px; max-width: 150px; }
          .marquee-item a { height: 64px; }
        }
        @media (prefers-reduced-motion: reduce) {
          .marquee-track { animation: none; }
        }
      `}</style>

      <h1 className="heading">trusted by leading clients</h1>

      <div className="marquee-viewport">
        <ul className="marquee-track" aria-label="Client logos (scrolling)">
          {track.map((c, i) => (
            <li key={i} className="marquee-item">
              <a href={c.url} aria-label={`Visit ${c.name}`} title={c.name}>
                <img
                  className="logo gray"
                  src={c.gray}
                  alt={c.name}
                  height="64"
                  loading="lazy"
                  decoding="async"
                />
                <img
                  className="logo color"
                  src={c.color}
                  alt=""
                  height="64"
                  loading="lazy"
                  decoding="async"
                  aria-hidden="true"
                />
              </a>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

/* ─────────────────────────────── home page ──────────────────────── */
export default function Home() {
  /* back-to-top */
  const [showTop, setShowTop] = useState(false);
  useEffect(() => {
    const onScroll = () => setShowTop(window.scrollY > 200);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  const scrollToTop = () =>
    window.scrollTo({ top: 0, behavior: "smooth" });

  /* electrical-company services */
  const services = [
    {
      icon: "bolt",
      title: "Electrical Installations",
      description: "Comprehensive LV / MV installs for homes & industry.",
      to: "/services/electrical-wiring",
    },
    {
      icon: "charging-station",
      title: "Generator Installation",
      description: "Reliable backup power: supply • install • maintain.",
      to: "/services/power-backup-systems",
    },
    {
      icon: "lightbulb",
      title: "Lighting Installation",
      description: "High-mast, stadium & architectural lighting.",
      to: "/services/lighting-installation",
    },
    {
      icon: "project-diagram",
      title: "Motor Control Centres",
      description: "Custom MCC design & build for process automation.",
      to: "/services/circuit-design",
    },
    {
      icon: "tools",
      title: "Routine Maintenance",
      description: "Preventative programmes that keep you running.",
      to: "/services/safety-inspection-compliance",
    },
  ];

  /* showcase projects */
  const projects = [
    {
      src: "LPG consumption customers3.jpg",
      title: "LPG Electrical Vaporticers installation",
      subtitle: "Francistown Depot",
    },
    {
      src: "LPG consumption customers5.jpg",
      title: "Stainless Distribution Board",
      subtitle: "Outdoor Hazardous-Area DB",
    },
    {
      src: "LPG consumption customers8.jpg",
      title: "6 kW Residential Solar",
      subtitle: "Pretoria East",
    },
    {
      src: "LPG consumption customers9.jpg",
      title: "5 kVA Hybrid Inverter",
      subtitle: "Wall-Mount LiFePO₄ Bank",
    },
    {
      src: "Panel wiring1.jpg",
      title: "LPG Pump MCC – Front",
      subtitle: "Operator Interface Panel",
    },
    {
      src: "Tzaneen LPG depot installation3.jpg",
      title: "LPG filling plant control",
      subtitle: "Construction Phase",
    },
  ];

  return (
    <>
      {/* ───── HERO SLIDER ───── */}
      <section className="home" id="home">
        <Swiper
          modules={[Navigation, Autoplay]}
          loop
          autoplay={{ delay: 8000, disableOnInteraction: false }}
          navigation
          className="home-slider"
        >
          {[
            {
              img: "home-slide-1.jpg",
              title: "Technology that Transforms Lives",
              text:
                "Level 1 BBBEE electrical contractor delivering innovative, energy-efficient solutions.",
            },
            {
              img: "HOME2.jpg",
              title: "End-to-End Electrical Expertise",
              text:
                "From design to maintenance, we power homes, factories and stadiums across Southern Africa.",
            },
            {
              img: "HOME3.jpg",
              title: "Automation Components In-Stock",
              text:
                "Browse our Variable Speed Drives, contactors and PLC solutions today.",
            },
          ].map((s, index) => (
            <SwiperSlide key={index}>
              <section
                className="slide"
                style={{
                  background: `url('${pub}/${s.img}') no-repeat center/cover`,
                }}
              >
                <div className="content">
                  <h3>{s.title}</h3>
                  <p>{s.text}</p>
                  <Link to="/about" className="btn">
                    get started
                  </Link>
                </div>
              </section>
            </SwiperSlide>
          ))}
        </Swiper>
      </section>

      {/* ───── ABOUT SNIPPET ───── */}
      <section className="about" id="about">
        <h1 className="heading"> about us </h1>
        <div className="row">
          <div className="video">
            <video src={`${pub}/Circut.mp4`} loop muted autoPlay />
          </div>
          <div className="content">
            <h3>We provide the safe, efficient solutions you need</h3>
            <p>
              Mosike Electrical Contractors is a 100&nbsp;% black-owned,
              black-directed company founded in&nbsp;2002. We specialise in
              electrical design, automation installation and maintenance of
              low-, medium- and high-voltage equipment.
            </p>
            <Link to="/about" className="btn">
              read more
            </Link>
          </div>
        </div>

        <div className="box-container">
          {[
            { num: "23+", text: "years of experience" },
            { num: "400+", text: "projects completed" },
            { num: "100+", text: "satisfied clients" },
            { num: "11", text: "skilled team members" },
          ].map(({ num, text }, index) => (
            <div className="box" key={index}>
              <h3>{num}</h3>
              <p>{text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ───── SERVICES ───── */}
      <section className="services" id="services">
        <h1 className="heading">
          <span>our</span> services
        </h1>
        <div className="box-container">
          {services.map((svc, index) => (
            <ServiceCard key={index} {...svc} />
          ))}
        </div>
      </section>

      {/* ───── PROJECTS ───── */}
      <section className="projects" id="projects">
        <h1 className="heading"> our projects </h1>
        <div className="box-container">
          {projects.map((proj, index) => (
            <ProjectCard key={index} {...proj} />
          ))}
        </div>
      </section>

      {/* ───── CLIENTS (MARQUEE) ───── */}
      <Clients />

      {/* ───── REVIEWS ───── */}
      <section className="reviews">
        <h1 className="heading"> clients reviews </h1>
        <Swiper
          modules={[Autoplay]}
          loop
          autoplay={{ delay: 8000, disableOnInteraction: false }}
          className="reviews-slider"
          slidesPerView={1}
        >
          {testimonials.map((rev, index) => (
            <SwiperSlide key={index}>
              <section className="slide">
                {rev.rating > 0 && (
                  <div className="review-stars" aria-label={`${rev.rating} out of 5 stars`}>
                    {Array.from({ length: 5 }).map((_, i) => (
                      <i
                        key={i}
                        className={i < rev.rating ? "fas fa-star" : "far fa-star"}
                      />
                    ))}
                  </div>
                )}
                <p>"{rev.text}"</p>
                <h3>
                  {rev.name}
                  {rev.location ? `, ${rev.location}` : ""}
                </h3>
                {rev.service && <span className="review-service">{rev.service}</span>}
              </section>
            </SwiperSlide>
          ))}
        </Swiper>

        <div className="reviews-cta">
          <p>Worked with us? We'd love to hear about it.</p>
          <Link to="/share-your-experience" className="btn">
            <i className="fas fa-pen" /> Share Your Experience
          </Link>
        </div>
      </section>

      {/* ───── CONTACT COMPONENT ───── */}
      <Contact />

      {/* ───── BACK TO TOP ───── */}
      {showTop && (
        <div className="back-to-top visible" onClick={scrollToTop}>
          <i className="fas fa-arrow-up" />
        </div>
      )}
    </>
  );
}
